import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas, isRutaRestricted } from '../lib/rutas';
import { TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight, Calendar, Filter, RefreshCw, ChevronDown, ChevronUp, TriangleAlert as AlertTriangle, Banknote, Receipt, PiggyBank, Wallet, Users, BarChart3 } from 'lucide-react';
import { startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, format, differenceInDays, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type PeriodPreset = 'today' | 'this_week' | 'month_to_date' | 'this_month' | 'last_month' | 'last_3_months' | 'this_year' | 'all' | 'custom';

interface PeriodRange {
  start: Date;
  end: Date;
}

interface FinancialMetrics {
  totalLoaned: number;
  newLoans: number;
  renewedLoans: number;
  totalReceived: number;
  totalLateFees: number;
  totalEntries: number;
  totalExpenses: number;
  totalWithdrawals: number;
  netProfit: number;
  returnRate: number;
  pendingVerificationAmount: number;
  pendingVerificationCount: number;
}

interface MonthlyData {
  month: string;
  label: string;
  inflows: number;
  outflows: number;
  net: number;
}

interface RutaBreakdown {
  ruta: number;
  loaned: number;
  received: number;
  expenses: number;
  net: number;
  returnRate: number;
}

interface PortfolioHealth {
  totalReceivable: number;
  overdueAmount: number;
  overdueCount: number;
  activeContracts: number;
  defaultRate: number;
}

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  today: 'Hoje',
  this_week: 'Esta Semana',
  month_to_date: 'Mes Atual (ate hoje)',
  this_month: 'Este Mes (completo)',
  last_month: 'Ultimo Mes',
  last_3_months: 'Ultimos 3 Meses',
  this_year: 'Este Ano',
  all: 'Todo Periodo',
  custom: 'Personalizado',
};

function getPeriodRange(preset: PeriodPreset, customStart?: Date, customEnd?: Date): PeriodRange {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'this_week':
      return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'month_to_date':
      return { start: startOfMonth(now), end: endOfDay(now) };
    case 'this_month':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'last_month': {
      const lastMonth = subMonths(now, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    }
    case 'last_3_months':
      return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
    case 'this_year':
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    case 'all':
      return { start: new Date(2020, 0, 1), end: now };
    case 'custom':
      return {
        start: customStart ? startOfDay(customStart) : startOfMonth(now),
        end: customEnd ? endOfDay(customEnd) : endOfDay(now),
      };
  }
}

function getPreviousPeriodRange(current: PeriodRange): PeriodRange {
  const days = differenceInDays(current.end, current.start) + 1;
  return {
    start: subDays(current.start, days),
    end: subDays(current.start, 1),
  };
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPercent(value: number): string {
  if (!isFinite(value)) return '--';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function VariationBadge({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  const pct = previous === 0 ? (current > 0 ? 100 : -100) : ((current - previous) / Math.abs(previous)) * 100;
  const isPositive = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {formatPercent(pct)}
    </span>
  );
}

export default function FinancialDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('month_to_date');
  const [customStartDate, setCustomStartDate] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [metrics, setMetrics] = useState<FinancialMetrics | null>(null);
  const [prevMetrics, setPrevMetrics] = useState<FinancialMetrics | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [rutaBreakdown, setRutaBreakdown] = useState<RutaBreakdown[]>([]);
  const [portfolioHealth, setPortfolioHealth] = useState<PortfolioHealth | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [recentInflows, setRecentInflows] = useState<any[]>([]);
  const [recentOutflows, setRecentOutflows] = useState<any[]>([]);

  const periodRange = useMemo(() => {
    const cStart = customStartDate ? new Date(customStartDate + 'T00:00:00') : undefined;
    const cEnd = customEndDate ? new Date(customEndDate + 'T00:00:00') : undefined;
    return getPeriodRange(periodPreset, cStart, cEnd);
  }, [periodPreset, customStartDate, customEndDate]);
  const prevPeriodRange = useMemo(() => getPreviousPeriodRange(periodRange), [periodRange]);

  useEffect(() => {
    if (user) {
      fetchAllData();
    }
  }, [user, periodPreset, selectedRuta, customStartDate, customEndDate]);

  useEffect(() => {
    if (isRutaRestricted(user)) {
      const visible = getVisibleRutas(user);
      if (!visible.includes(selectedRuta)) setSelectedRuta(visible[0]);
    }
  }, [user]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchMetrics(periodRange).then(setMetrics),
        compareEnabled ? fetchMetrics(prevPeriodRange).then(setPrevMetrics) : Promise.resolve(setPrevMetrics(null)),
        fetchMonthlyData(),
        fetchRutaBreakdown(),
        fetchPortfolioHealth(),
        fetchRecentInflows(),
        fetchRecentOutflows(),
      ]);
    } catch (err) {
      console.error('Error fetching financial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async (range: PeriodRange): Promise<FinancialMetrics> => {
    if (!user) return emptyMetrics();

    let movementsQuery = supabase
      .from('cash_movements')
      .select('amount, kind')
      .eq('unit_id', user.unit_id)
      .gte('created_at', range.start.toISOString())
      .lte('created_at', range.end.toISOString());

    if (selectedRuta > 0) {
      movementsQuery = movementsQuery.eq('ruta', selectedRuta);
    }

    const { data: movements } = await movementsQuery;

    const { data: txSummary } = await supabase.rpc('get_financial_transactions_summary', {
      p_unit_id: user.unit_id,
      p_start_date: range.start.toISOString(),
      p_end_date: range.end.toISOString(),
      p_ruta: selectedRuta > 0 ? selectedRuta : null,
    });

    const { data: pendingSummary } = await supabase.rpc('get_financial_pending_summary', {
      p_unit_id: user.unit_id,
      p_start_date: range.start.toISOString(),
      p_end_date: range.end.toISOString(),
      p_ruta: selectedRuta > 0 ? selectedRuta : null,
    });

    const totalReceived = (txSummary || []).reduce((s: number, r: any) => s + Number(r.total_amount), 0);
    const totalLateFees = (txSummary || []).reduce((s: number, r: any) => s + Number(r.total_late_fee), 0);

    const pendingRow = (pendingSummary || [])[0];
    const pendingVerificationAmount = pendingRow ? Number(pendingRow.total_amount) + Number(pendingRow.total_late_fee) : 0;
    const pendingVerificationCount = pendingRow ? Number(pendingRow.tx_count) : 0;

    const newLoans = (movements || [])
      .filter(m => m.kind === 'new_sale')
      .reduce((sum, m) => sum + Math.abs(m.amount), 0);

    const renewedLoans = (movements || [])
      .filter(m => m.kind === 'renewed_sale')
      .reduce((sum, m) => sum + Math.abs(m.amount), 0);

    const totalEntries = (movements || [])
      .filter(m => m.kind === 'entry')
      .reduce((sum, m) => sum + m.amount, 0);

    const totalExpenses = (movements || [])
      .filter(m => m.kind === 'expense')
      .reduce((sum, m) => sum + Math.abs(m.amount), 0);

    const totalWithdrawals = (movements || [])
      .filter(m => m.kind === 'withdrawal')
      .reduce((sum, m) => sum + Math.abs(m.amount), 0);

    const totalLoaned = newLoans + renewedLoans;
    const totalInflows = totalReceived + totalLateFees + totalEntries;
    const totalOutflows = totalLoaned + totalExpenses + totalWithdrawals;
    const netProfit = totalInflows - totalOutflows;
    const returnRate = totalLoaned > 0 ? (totalReceived / totalLoaned) * 100 : 0;

    return {
      totalLoaned,
      newLoans,
      renewedLoans,
      totalReceived: totalReceived + totalLateFees,
      totalLateFees,
      totalEntries,
      totalExpenses,
      totalWithdrawals,
      netProfit,
      returnRate,
      pendingVerificationAmount,
      pendingVerificationCount,
    };
  };

  const fetchMonthlyData = async () => {
    if (!user) return;
    const months: MonthlyData[] = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const start = startOfMonth(monthDate);
      const end = endOfMonth(monthDate);

      let movQuery = supabase
        .from('cash_movements')
        .select('amount, kind')
        .eq('unit_id', user.unit_id)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (selectedRuta > 0) movQuery = movQuery.eq('ruta', selectedRuta);

      const { data: movs } = await movQuery;

      const { data: txSummary } = await supabase.rpc('get_financial_transactions_summary', {
        p_unit_id: user.unit_id,
        p_start_date: start.toISOString(),
        p_end_date: end.toISOString(),
        p_ruta: selectedRuta > 0 ? selectedRuta : null,
      });

      const txInflows = (txSummary || []).reduce((s: number, r: any) => s + Number(r.total_amount) + Number(r.total_late_fee), 0);

      const inflows = txInflows
        + (movs || []).filter(m => m.kind === 'entry').reduce((s, m) => s + m.amount, 0);

      const outflows = (movs || [])
        .filter(m => ['new_sale', 'renewed_sale', 'expense', 'withdrawal'].includes(m.kind))
        .reduce((s, m) => s + Math.abs(m.amount), 0);

      months.push({
        month: format(monthDate, 'yyyy-MM'),
        label: format(monthDate, 'MMM/yy', { locale: ptBR }),
        inflows,
        outflows,
        net: inflows - outflows,
      });
    }

    setMonthlyData(months);
  };

  const fetchRutaBreakdown = async () => {
    if (!user) return;

    const { data: movs } = await supabase
      .from('cash_movements')
      .select('amount, kind, ruta')
      .eq('unit_id', user.unit_id)
      .gte('created_at', periodRange.start.toISOString())
      .lte('created_at', periodRange.end.toISOString());

    const { data: txSummary } = await supabase.rpc('get_financial_transactions_summary', {
      p_unit_id: user.unit_id,
      p_start_date: periodRange.start.toISOString(),
      p_end_date: periodRange.end.toISOString(),
      p_ruta: null,
    });

    const txByRuta = new Map<number, { amount: number; lateFee: number }>();
    for (const row of (txSummary || [])) {
      txByRuta.set(row.ruta, { amount: Number(row.total_amount), lateFee: Number(row.total_late_fee) });
    }

    const breakdown: RutaBreakdown[] = [];

    for (let ruta = 1; ruta <= 8; ruta++) {
      const rutaMovs = (movs || []).filter(m => m.ruta === ruta);

      const loaned = rutaMovs
        .filter(m => m.kind === 'new_sale' || m.kind === 'renewed_sale')
        .reduce((s, m) => s + Math.abs(m.amount), 0);

      const expenses = rutaMovs
        .filter(m => m.kind === 'expense' || m.kind === 'withdrawal')
        .reduce((s, m) => s + Math.abs(m.amount), 0);

      const txData = txByRuta.get(ruta);
      const received = txData ? txData.amount + txData.lateFee : 0;

      if (loaned > 0 || received > 0 || expenses > 0) {
        breakdown.push({
          ruta,
          loaned,
          received,
          expenses,
          net: received - loaned - expenses,
          returnRate: loaned > 0 ? (received / loaned) * 100 : 0,
        });
      }
    }

    setRutaBreakdown(breakdown);
  };

  const fetchPortfolioHealth = async () => {
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];

    const { data: pendingInstallments } = await supabase
      .from('installments')
      .select('amount, paid_amount, due_date, status, contract:contracts!inner(ruta)')
      .eq('unit_id', user.unit_id)
      .neq('status', 'paid');

    let filtered = pendingInstallments || [];
    if (selectedRuta > 0) {
      filtered = filtered.filter(i => (i.contract as any)?.ruta === selectedRuta);
    }

    const totalReceivable = filtered.reduce((s, i) => {
      return s + (i.amount - (i.paid_amount || 0));
    }, 0);

    const overdue = filtered.filter(i => i.due_date < today);
    const overdueAmount = overdue.reduce((s, i) => s + (i.amount - (i.paid_amount || 0)), 0);

    let contractsQuery = supabase
      .from('contracts')
      .select('*', { count: 'exact', head: true })
      .eq('unit_id', user.unit_id)
      .eq('approval_status', 'approved')
      .eq('status', 'active');

    if (selectedRuta > 0) {
      contractsQuery = contractsQuery.eq('ruta', selectedRuta);
    }

    const { count: activeContracts } = await contractsQuery;

    const totalInstallments = filtered.length;
    const defaultRate = totalInstallments > 0 ? (overdue.length / totalInstallments) * 100 : 0;

    setPortfolioHealth({
      totalReceivable,
      overdueAmount,
      overdueCount: overdue.length,
      activeContracts: activeContracts || 0,
      defaultRate,
    });
  };

  const fetchRecentInflows = async () => {
    if (!user) return;

    let query = supabase
      .from('payment_transactions')
      .select(`
        id, amount, late_fee, verified_at,
        contract:contracts!payment_transactions_contract_id_fkey(client_name, ruta, code)
      `)
      .eq('unit_id', user.unit_id)
      .eq('status', 'approved')
      .gte('verified_at', periodRange.start.toISOString())
      .lte('verified_at', periodRange.end.toISOString())
      .order('verified_at', { ascending: false })
      .limit(50);

    const { data } = await query;

    let filtered = data || [];
    if (selectedRuta > 0) {
      filtered = filtered.filter(t => (t.contract as any)?.ruta === selectedRuta);
    }

    setRecentInflows(filtered.slice(0, 15));
  };

  const fetchRecentOutflows = async () => {
    if (!user) return;

    let query = supabase
      .from('cash_movements')
      .select('id, amount, kind, description, created_at, ruta')
      .eq('unit_id', user.unit_id)
      .in('kind', ['expense', 'withdrawal'])
      .gte('created_at', periodRange.start.toISOString())
      .lte('created_at', periodRange.end.toISOString())
      .order('created_at', { ascending: false });

    if (selectedRuta > 0) query = query.eq('ruta', selectedRuta);

    const { data } = await query;
    setRecentOutflows(data || []);
  };

  const emptyMetrics = (): FinancialMetrics => ({
    totalLoaned: 0, newLoans: 0, renewedLoans: 0, totalReceived: 0,
    totalLateFees: 0, totalEntries: 0, totalExpenses: 0, totalWithdrawals: 0,
    netProfit: 0, returnRate: 0, pendingVerificationAmount: 0, pendingVerificationCount: 0,
  });

  const maxMonthlyValue = useMemo(() => {
    return Math.max(...monthlyData.map(m => Math.max(m.inflows, m.outflows)), 1);
  }, [monthlyData]);

  const outflowDistribution = useMemo(() => {
    if (!metrics) return [];
    const total = metrics.newLoans + metrics.renewedLoans + metrics.totalExpenses + metrics.totalWithdrawals;
    if (total === 0) return [];
    return [
      { label: 'Novos Emprestimos', value: metrics.newLoans, pct: (metrics.newLoans / total) * 100, color: 'bg-blue-500' },
      { label: 'Renovacoes', value: metrics.renewedLoans, pct: (metrics.renewedLoans / total) * 100, color: 'bg-cyan-500' },
      { label: 'Gastos', value: metrics.totalExpenses, pct: (metrics.totalExpenses / total) * 100, color: 'bg-amber-500' },
      { label: 'Retiradas', value: metrics.totalWithdrawals, pct: (metrics.totalWithdrawals / total) * 100, color: 'bg-red-500' },
    ].filter(d => d.value > 0);
  }, [metrics]);

  const kindLabel = (kind: string) => {
    switch (kind) {
      case 'new_sale': return 'Novo Emprestimo';
      case 'renewed_sale': return 'Renovacao';
      case 'expense': return 'Gasto';
      case 'withdrawal': return 'Retirada';
      case 'entry': return 'Entrada';
      default: return kind;
    }
  };

  const kindColor = (kind: string) => {
    switch (kind) {
      case 'new_sale': return 'text-blue-700 bg-blue-50';
      case 'renewed_sale': return 'text-cyan-700 bg-cyan-50';
      case 'expense': return 'text-amber-700 bg-amber-50';
      case 'withdrawal': return 'text-red-700 bg-red-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-500" />
          <span className="text-gray-500">Carregando dados financeiros...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-600" />
            Dashboard Financeiro
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Visao consolidada de todas as movimentacoes financeiras
          </p>
        </div>
        {loading && (
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <select
            value={periodPreset}
            onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {Object.entries(PERIOD_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        {periodPreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <span className="text-xs text-gray-400">ate</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        )}

        {periodPreset !== 'custom' && (
          <span className="text-xs text-gray-400">
            {format(periodRange.start, 'dd/MM/yyyy')} - {format(periodRange.end, 'dd/MM/yyyy')}
          </span>
        )}

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={selectedRuta}
            onChange={(e) => setSelectedRuta(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {!isRutaRestricted(user) && <option value={0}>Todas as Rutas</option>}
            {getVisibleRutas(user).map(r => (
              <option key={r} value={r}>Ruta {r}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
          <input
            type="checkbox"
            checked={compareEnabled}
            onChange={(e) => setCompareEnabled(e.target.checked)}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Comparar com periodo anterior
        </label>
      </div>

      {/* KPI Cards */}
      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          <KPICard
            icon={<Banknote className="w-5 h-5 text-blue-600" />}
            label="Total Emprestado"
            value={formatCurrency(metrics.totalLoaned)}
            bgColor="bg-blue-50"
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.totalLoaned} previous={prevMetrics.totalLoaned} /> : undefined}
          />
          <KPICard
            icon={<DollarSign className="w-5 h-5 text-emerald-600" />}
            label="Total Recebido"
            value={formatCurrency(metrics.totalReceived)}
            bgColor="bg-emerald-50"
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.totalReceived} previous={prevMetrics.totalReceived} /> : undefined}
          />
          <KPICard
            icon={<Receipt className="w-5 h-5 text-amber-600" />}
            label="Gastos"
            value={formatCurrency(metrics.totalExpenses)}
            bgColor="bg-amber-50"
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.totalExpenses} previous={prevMetrics.totalExpenses} /> : undefined}
          />
          <KPICard
            icon={<Wallet className="w-5 h-5 text-red-600" />}
            label="Retiradas"
            value={formatCurrency(metrics.totalWithdrawals)}
            bgColor="bg-red-50"
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.totalWithdrawals} previous={prevMetrics.totalWithdrawals} /> : undefined}
          />
          <KPICard
            icon={<PiggyBank className="w-5 h-5 text-teal-600" />}
            label="Lucro Liquido"
            value={formatCurrency(metrics.netProfit)}
            bgColor={metrics.netProfit >= 0 ? 'bg-teal-50' : 'bg-red-50'}
            valueColor={metrics.netProfit >= 0 ? 'text-teal-700' : 'text-red-700'}
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.netProfit} previous={prevMetrics.netProfit} /> : undefined}
          />
          <KPICard
            icon={<TrendingUp className="w-5 h-5 text-indigo-600" />}
            label="Taxa de Retorno"
            value={`${metrics.returnRate.toFixed(1)}%`}
            bgColor="bg-indigo-50"
            comparison={compareEnabled && prevMetrics ? <VariationBadge current={metrics.returnRate} previous={prevMetrics.returnRate} /> : undefined}
          />
          <KPICard
            icon={<AlertTriangle className="w-5 h-5 text-orange-600" />}
            label="Pend. Verificacao"
            value={formatCurrency(metrics.pendingVerificationAmount)}
            bgColor="bg-orange-50"
            subtitle={`${metrics.pendingVerificationCount} transacoes`}
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Evolucao Mensal (6 meses)</h3>
          <div className="flex items-end gap-3 h-48">
            {monthlyData.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex gap-0.5 items-end h-36">
                  <div className="flex-1 flex flex-col justify-end">
                    <div
                      className="w-full bg-emerald-400 rounded-t transition-all duration-500"
                      style={{ height: `${(m.inflows / maxMonthlyValue) * 100}%`, minHeight: m.inflows > 0 ? '4px' : '0' }}
                      title={`Entradas: ${formatCurrency(m.inflows)}`}
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-end">
                    <div
                      className="w-full bg-red-400 rounded-t transition-all duration-500"
                      style={{ height: `${(m.outflows / maxMonthlyValue) * 100}%`, minHeight: m.outflows > 0 ? '4px' : '0' }}
                      title={`Saidas: ${formatCurrency(m.outflows)}`}
                    />
                  </div>
                </div>
                <span className="text-[10px] text-gray-500 uppercase">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400" /> Entradas</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400" /> Saidas</span>
          </div>
          {/* Net line */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex justify-between text-xs">
              {monthlyData.map(m => (
                <span key={m.month} className={`font-medium ${m.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {m.net >= 0 ? '+' : ''}{(m.net / 1000).toFixed(0)}k
                </span>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Saldo liquido por mes</p>
          </div>
        </div>

        {/* Outflow Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribuicao de Saidas</h3>
          {outflowDistribution.length > 0 ? (
            <>
              {/* Stacked bar */}
              <div className="w-full h-6 rounded-full overflow-hidden flex">
                {outflowDistribution.map((d, i) => (
                  <div
                    key={i}
                    className={`${d.color} transition-all duration-500`}
                    style={{ width: `${d.pct}%` }}
                    title={`${d.label}: ${formatCurrency(d.value)} (${d.pct.toFixed(1)}%)`}
                  />
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {outflowDistribution.map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded ${d.color}`} />
                      <span className="text-xs text-gray-600">{d.label}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-800">{formatCurrency(d.value)}</span>
                      <span className="text-[10px] text-gray-400 ml-1">({d.pct.toFixed(0)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Sem dados no periodo</p>
          )}
        </div>
      </div>

      {/* Detailed Inflows */}
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          onClick={() => setExpandedSection(expandedSection === 'inflows' ? null : 'inflows')}
          className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-gray-700">Entradas Detalhadas</h3>
              <p className="text-xs text-gray-400">
                Parcelas: {formatCurrency(metrics?.totalReceived || 0)} | Entradas avulsas: {formatCurrency(metrics?.totalEntries || 0)}
              </p>
            </div>
          </div>
          {expandedSection === 'inflows' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {expandedSection === 'inflows' && (
          <div className="border-t border-gray-100 p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-600 font-medium">Parcelas Recebidas</p>
                <p className="text-lg font-bold text-emerald-800">{formatCurrency((metrics?.totalReceived || 0) - (metrics?.totalLateFees || 0))}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-600 font-medium">Juros/Multas</p>
                <p className="text-lg font-bold text-yellow-800">{formatCurrency(metrics?.totalLateFees || 0)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600 font-medium">Entradas Avulsas</p>
                <p className="text-lg font-bold text-blue-800">{formatCurrency(metrics?.totalEntries || 0)}</p>
              </div>
            </div>
            {recentInflows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">Cliente</th>
                      <th className="pb-2 font-medium">Codigo</th>
                      <th className="pb-2 font-medium">Ruta</th>
                      <th className="pb-2 font-medium text-right">Valor</th>
                      <th className="pb-2 font-medium text-right">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInflows.map(t => (
                      <tr key={t.id} className="border-b border-gray-50">
                        <td className="py-2 text-gray-800">{(t.contract as any)?.client_name || '--'}</td>
                        <td className="py-2 text-gray-500">{(t.contract as any)?.code || '--'}</td>
                        <td className="py-2 text-gray-500">{(t.contract as any)?.ruta || '--'}</td>
                        <td className="py-2 text-right font-medium text-emerald-700">
                          {formatCurrency(t.amount + (t.late_fee || 0))}
                        </td>
                        <td className="py-2 text-right text-gray-500">
                          {t.verified_at ? format(new Date(t.verified_at), 'dd/MM/yy HH:mm') : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detailed Outflows */}
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          onClick={() => setExpandedSection(expandedSection === 'outflows' ? null : 'outflows')}
          className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-gray-700">Saidas Detalhadas</h3>
              <p className="text-xs text-gray-400">
                Gastos: {formatCurrency(metrics?.totalExpenses || 0)} | Retiradas: {formatCurrency(metrics?.totalWithdrawals || 0)} | Total: {formatCurrency((metrics?.totalExpenses || 0) + (metrics?.totalWithdrawals || 0))}
              </p>
            </div>
          </div>
          {expandedSection === 'outflows' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {expandedSection === 'outflows' && (
          <div className="border-t border-gray-100 p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-amber-600 font-medium">Gastos Operacionais</p>
                <p className="text-lg font-bold text-amber-800">{formatCurrency(metrics?.totalExpenses || 0)}</p>
                <p className="text-xs text-amber-500 mt-0.5">{recentOutflows.filter(m => m.kind === 'expense').length} registros</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs text-red-600 font-medium">Retiradas</p>
                <p className="text-lg font-bold text-red-800">{formatCurrency(metrics?.totalWithdrawals || 0)}</p>
                <p className="text-xs text-red-500 mt-0.5">{recentOutflows.filter(m => m.kind === 'withdrawal').length} registros</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-medium">Total Gastos + Retiradas</p>
                <p className="text-lg font-bold text-gray-800">{formatCurrency((metrics?.totalExpenses || 0) + (metrics?.totalWithdrawals || 0))}</p>
                <p className="text-xs text-gray-500 mt-0.5">{recentOutflows.length} registros</p>
              </div>
            </div>
            {recentOutflows.length > 0 && (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">Tipo</th>
                      <th className="pb-2 font-medium">Descricao</th>
                      <th className="pb-2 font-medium">Ruta</th>
                      <th className="pb-2 font-medium text-right">Valor</th>
                      <th className="pb-2 font-medium text-right">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOutflows.map(m => (
                      <tr key={m.id} className="border-b border-gray-50">
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${kindColor(m.kind)}`}>
                            {kindLabel(m.kind)}
                          </span>
                        </td>
                        <td className="py-2 text-gray-800 max-w-[200px] truncate">{m.description || '--'}</td>
                        <td className="py-2 text-gray-500">{m.ruta}</td>
                        <td className="py-2 text-right font-medium text-red-700">{formatCurrency(Math.abs(m.amount))}</td>
                        <td className="py-2 text-right text-gray-500">
                          {format(new Date(m.created_at), 'dd/MM/yy HH:mm')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {recentOutflows.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Nenhum gasto ou retirada registrado neste periodo</p>
            )}
          </div>
        )}
      </div>

      {/* Ruta Breakdown Table */}
      {rutaBreakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            Desempenho por Ruta
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="pb-3 font-medium">Ruta</th>
                  <th className="pb-3 font-medium text-right">Emprestado</th>
                  <th className="pb-3 font-medium text-right">Recebido</th>
                  <th className="pb-3 font-medium text-right">Gastos</th>
                  <th className="pb-3 font-medium text-right">Saldo</th>
                  <th className="pb-3 font-medium text-right">Retorno</th>
                </tr>
              </thead>
              <tbody>
                {rutaBreakdown.map(r => {
                  const best = rutaBreakdown.reduce((a, b) => a.returnRate > b.returnRate ? a : b);
                  const worst = rutaBreakdown.reduce((a, b) => a.returnRate < b.returnRate ? a : b);
                  const isBest = r.ruta === best.ruta && rutaBreakdown.length > 1;
                  const isWorst = r.ruta === worst.ruta && rutaBreakdown.length > 1;
                  return (
                    <tr key={r.ruta} className={`border-b border-gray-50 ${isBest ? 'bg-emerald-50/50' : isWorst ? 'bg-red-50/50' : ''}`}>
                      <td className="py-3 font-medium text-gray-800">Ruta {r.ruta}</td>
                      <td className="py-3 text-right text-gray-700">{formatCurrency(r.loaned)}</td>
                      <td className="py-3 text-right text-emerald-700 font-medium">{formatCurrency(r.received)}</td>
                      <td className="py-3 text-right text-amber-700">{formatCurrency(r.expenses)}</td>
                      <td className={`py-3 text-right font-medium ${r.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatCurrency(r.net)}
                      </td>
                      <td className="py-3 text-right">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.returnRate >= 100 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                          {r.returnRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr className="border-t-2 border-gray-200 font-semibold">
                  <td className="py-3 text-gray-900">Total</td>
                  <td className="py-3 text-right text-gray-900">{formatCurrency(rutaBreakdown.reduce((s, r) => s + r.loaned, 0))}</td>
                  <td className="py-3 text-right text-emerald-800">{formatCurrency(rutaBreakdown.reduce((s, r) => s + r.received, 0))}</td>
                  <td className="py-3 text-right text-amber-800">{formatCurrency(rutaBreakdown.reduce((s, r) => s + r.expenses, 0))}</td>
                  <td className={`py-3 text-right ${rutaBreakdown.reduce((s, r) => s + r.net, 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                    {formatCurrency(rutaBreakdown.reduce((s, r) => s + r.net, 0))}
                  </td>
                  <td className="py-3 text-right text-gray-700">--</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Portfolio Health */}
      {portfolioHealth && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-gray-500" />
            Saude da Carteira
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500">Total a Receber</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(portfolioHealth.totalReceivable)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-xs text-red-600">Parcelas Vencidas</p>
              <p className="text-xl font-bold text-red-800 mt-1">{formatCurrency(portfolioHealth.overdueAmount)}</p>
              <p className="text-xs text-red-500 mt-0.5">{portfolioHealth.overdueCount} parcelas</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-blue-600">Contratos Ativos</p>
              <p className="text-xl font-bold text-blue-800 mt-1">{portfolioHealth.activeContracts}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-xs text-amber-600">Taxa de Inadimplencia</p>
              <p className="text-xl font-bold text-amber-800 mt-1">{portfolioHealth.defaultRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon, label, value, bgColor, valueColor, comparison, subtitle }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
  valueColor?: string;
  comparison?: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 ${bgColor} rounded-lg flex items-center justify-center`}>
          {icon}
        </div>
        {comparison}
      </div>
      <p className={`text-lg font-bold ${valueColor || 'text-gray-900'} truncate`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {subtitle && <p className="text-[10px] text-gray-400">{subtitle}</p>}
    </div>
  );
}
